import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarPlus, CalendarRange, type LucideIcon } from 'lucide-react';
import { useUser } from '@/shared/hooks/useAuth';
import { ROUTES } from '@/routes/paths';
import { cn } from '@/shared/utils/utils';

// ─────────────────────────────────────────────────────────────────────────────
// The one dashboard header every role dashboard opens with (2026-10-23 —
// "dashboards look too plain"): a tinted banner that says whose desk this is
// and what day it is, the few figures that decide the day in a large type,
// and the actions everyone needs (Apply for Leave) in a fixed place instead of
// a strip floating above the page. Figures stay mono / tabular and ink-coloured
// — the tint is the chip's, never the number's.
// ─────────────────────────────────────────────────────────────────────────────

export type BannerTone = 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export interface BannerStat {
  label: string;
  /** Already formatted by the caller (formatINR, Indian-grouped numerals). */
  value: string;
  sub?: ReactNode;
  icon?: LucideIcon;
  tone?: BannerTone;
}

const CHIP_TONE: Record<BannerTone, string> = {
  primary: 'bg-primary-container text-primary-dim',
  success: 'bg-success/10 text-success-text',
  warning: 'bg-warning/10 text-warning-text',
  danger: 'bg-danger/10 text-danger-text',
  info: 'bg-info/10 text-info-text',
  neutral: 'bg-surface-container-highest text-on-surface-variant',
};

// Spelled out: Tailwind only ships class names it can find whole in source.
const STAT_COLS: Record<number, string> = {
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
  5: 'sm:grid-cols-3 lg:grid-cols-5',
  6: 'sm:grid-cols-3 lg:grid-cols-6',
};

/** "Good morning" / "Good afternoon" / "Good evening" in the viewer's clock. */
export function greetingFor(date = new Date()) {
  const h = date.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/** First name for a greeting — "Demo" from "Demo Sales BDE". */
export function firstNameOf(user: { displayName?: string | null; display_name?: string | null; username?: string | null } | null | undefined) {
  const full = String(user?.displayName || user?.display_name || user?.username || '').trim();
  return full.split(/\s+/)[0] || 'there';
}

function initialsOf(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || 'Z';
}

const DEPARTMENT_LABEL: Record<string, string> = {
  sales: 'Sales', dispatch: 'Dispatch', accounts: 'Accounts', hr: 'HR', it: 'IT & Systems',
  digital_marketing: 'Marketing', marketing: 'Marketing', management: 'Management', production: 'Production',
  operations: 'Operations', support: 'Support', general_manager: 'Management',
};

// ── Leave actions: in the banner when there is one, else a fallback strip ───
const LeaveHostContext = createContext<{ register: () => () => void } | null>(null);
export const LeaveHostProvider = LeaveHostContext.Provider;

/** Apply for Leave / My Leaves — every employee's own leave (client, 2026-10-23). */
export function LeaveActions({ compact = false }: { compact?: boolean }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Leave">
      <button
        type="button"
        onClick={() => navigate(`${ROUTES.leaves}?apply=1`)}
        className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-3.5 text-body-sm font-semibold text-on-primary shadow-glow transition-colors hover:bg-primary-dim focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <CalendarPlus className="h-4 w-4" aria-hidden /> Apply for Leave
      </button>
      {!compact && (
        <button
          type="button"
          onClick={() => navigate(ROUTES.leaves)}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-card/80 px-3.5 text-body-sm font-semibold text-on-surface backdrop-blur transition-colors hover:border-primary/30 hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <CalendarRange className="h-4 w-4 text-on-surface-variant" aria-hidden /> My Leaves
        </button>
      )}
    </div>
  );
}

/** One headline figure: label, tinted icon chip, the figure large and mono. */
export function StatTile({ stat: s }: { stat: BannerStat }) {
  const StatIcon = s.icon;
  return (
    <div className="rounded-card-sm border border-border/80 bg-card/90 px-4 py-3 shadow-xs backdrop-blur transition-shadow hover:shadow-surface-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-body-sm font-medium text-on-surface-variant">{s.label}</span>
        {StatIcon ? (
          <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', CHIP_TONE[s.tone || 'primary'])} aria-hidden>
            <StatIcon className="h-4 w-4" />
          </span>
        ) : null}
      </div>
      <div className="mt-1.5 truncate font-mono text-[22px] font-semibold leading-tight tabular-nums text-on-surface" title={s.value}>
        {s.value}
      </div>
      {s.sub ? <div className="mt-1 text-data-xs text-on-surface-subtle">{s.sub}</div> : null}
    </div>
  );
}

export function WelcomeBanner({
  eyebrow,
  title,
  subtitle,
  icon: Icon,
  stats = [],
  actions,
  showLeaveActions = true,
  variant = 'hero',
  children,
}: {
  /** Small line above the title; defaults to today's date · department. */
  eyebrow?: string;
  /** Defaults to "Good afternoon, <first name>". */
  title?: string;
  subtitle?: ReactNode;
  /** Shown instead of the initials avatar. */
  icon?: LucideIcon;
  stats?: BannerStat[];
  actions?: ReactNode;
  showLeaveActions?: boolean;
  /** 'section': the same figures as a plain card inside a page that already
   *  has its banner (the Team Dashboard's "My work" tab) — no greeting, avatar,
   *  tint or leave actions, so the page never shows two banners. */
  variant?: 'hero' | 'section';
  children?: ReactNode;
}) {
  const user = useUser();
  const host = useContext(LeaveHostContext);
  const isHero = variant === 'hero';
  const leaveHere = isHero && showLeaveActions;
  useEffect(() => (leaveHere && host ? host.register() : undefined), [host, leaveHere]);

  const name = String(user?.displayName || user?.username || '');
  const department = DEPARTMENT_LABEL[String(user?.department || '').toLowerCase()] || '';
  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const statCols = STAT_COLS[Math.min(stats.length, 6)] || STAT_COLS[4];

  if (!isHero) {
    return (
      <section className="mb-5 overflow-hidden rounded-card border border-border bg-card shadow-surface-1">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-surface-container-low/60 px-5 py-3">
          <div className="min-w-0">
            <h2 className="text-body-md font-semibold text-on-surface">{title || 'My desk'}</h2>
            {subtitle ? <div className="text-body-sm text-on-surface-variant">{subtitle}</div> : null}
          </div>
          {actions}
        </div>
        {stats.length > 0 && (
          <div className={cn('grid grid-cols-1 gap-3 p-4', statCols)}>
            {stats.map((s) => <StatTile key={s.label} stat={s} />)}
          </div>
        )}
        {children ? <div className="px-5 pb-5">{children}</div> : null}
      </section>
    );
  }

  return (
    <section className="relative mb-5 overflow-hidden rounded-card border border-primary/15 bg-gradient-to-br from-primary-container/80 via-card to-card shadow-surface-1">
      {/* Quiet depth in the corner — decoration, never information. */}
      <div aria-hidden className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-info/5 blur-3xl" />

      <div className="relative flex flex-wrap items-start justify-between gap-4 px-5 pb-4 pt-5">
        <div className="flex min-w-0 items-start gap-3.5">
          <span
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-dim text-body-lg font-semibold text-on-primary shadow-glow"
            aria-hidden
          >
            {Icon ? <Icon className="h-5 w-5" /> : initialsOf(name)}
          </span>
          <div className="min-w-0">
            <div className="text-label-caps uppercase tracking-wider text-primary-dim">
              {eyebrow || [today, department].filter(Boolean).join(' · ')}
            </div>
            <h1 className="mt-1 break-words text-[22px] font-semibold leading-tight tracking-tight text-on-surface">
              {title || `${greetingFor()}, ${firstNameOf(user as never)}`}
            </h1>
            {subtitle ? <div className="mt-1 max-w-3xl text-body-md text-on-surface-variant">{subtitle}</div> : null}
          </div>
        </div>
        {(actions || showLeaveActions) && (
          <div className="flex flex-wrap items-center gap-2">
            {actions}
            {showLeaveActions && <LeaveActions />}
          </div>
        )}
      </div>

      {stats.length > 0 && (
        <div className={cn('relative grid grid-cols-1 gap-3 px-5 pb-5', statCols)}>
          {stats.map((s) => <StatTile key={s.label} stat={s} />)}
        </div>
      )}
      {children ? <div className="relative px-5 pb-5">{children}</div> : null}
    </section>
  );
}
